import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Field, TextInput } from "@/components/site/Field";
import { IMAGES } from "@/lib/data";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [forgotEmail, setForgotEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (ready && user) nav("/admin"); }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password.trim());
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail, reset_base: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={IMAGES.logo} alt="Harbour Haze" className="h-16 w-16 mx-auto object-contain" />
          <h1 className="mt-4 font-head font-black text-3xl tracking-tight">{mode === "login" ? "Staff Login" : "Forgot password"}</h1>
          <p className="overline text-foreground/50 mt-1">Harbour Haze Admin</p>
        </div>

        {mode === "login" ? (
          <>
            <form onSubmit={submit} className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8 space-y-5" data-testid="admin-login-form">
              <Field label="Email"><TextInput data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" autoComplete="username" /></Field>
              <Field label="Password"><TextInput data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="current-password" /></Field>
              <button type="submit" disabled={loading} data-testid="login-submit"
                className="w-full font-head font-bold text-lg bg-foreground text-background rounded-full py-4 flex items-center justify-center gap-2 hover:bg-primary hover:text-foreground transition-colors disabled:opacity-60">
                <Lock size={16} /> {loading ? "Signing in…" : "Sign in"}
              </button>
              <button type="button" onClick={() => { setMode("forgot"); setForgotEmail(email); }} data-testid="forgot-password-link"
                className="w-full text-center font-mono-plex text-xs text-foreground/60 hover:text-foreground">
                Forgot your password?
              </button>
            </form>
          </>
        ) : (
          <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8" data-testid="forgot-panel">
            {sent ? (
              <div className="text-center space-y-3" data-testid="forgot-sent">
                <Mail size={36} className="mx-auto text-secondary" />
                <p className="font-head font-bold text-lg">Check your email</p>
                <p className="text-foreground/60 text-sm">If that address is registered, a reset link is on its way. The link expires in 1 hour.</p>
              </div>
            ) : (
              <form onSubmit={sendReset} className="space-y-5" data-testid="forgot-form">
                <p className="text-foreground/60 text-sm">Enter your account email and we'll send you a link to reset your password.</p>
                <Field label="Email"><TextInput data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="admin@harbourhaze.co.nz" /></Field>
                <button type="submit" disabled={sending} data-testid="forgot-submit"
                  className="w-full font-head font-bold text-lg bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-3.5 flex items-center justify-center gap-2 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
                  <Mail size={16} /> {sending ? "Sending…" : "Send reset link"}
                </button>
              </form>
            )}
            <button type="button" onClick={() => { setMode("login"); setSent(false); }} data-testid="back-to-login"
              className="w-full mt-6 flex items-center justify-center gap-1.5 font-mono-plex text-xs text-foreground/60 hover:text-foreground">
              <ArrowLeft size={13} /> Back to login
            </button>
          </div>
        )}

        <a href="/" className="block text-center mt-6 font-mono-plex text-xs text-foreground/50 hover:text-foreground">← Back to site</a>
      </div>
    </div>
  );
}
